import React, { useState, useRef, useEffect } from "react";
import "./AppCard.css";

// Reusable SVG logo components for a premium look
const JiraLogo = () => (
  <svg className="app-card__logo-svg" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 2L2 11.5L11.5 21L21 11.5L11.5 2Z" fill="#2563eb" />
    <path d="M11.5 6L6 11.5L11.5 17L17 11.5L11.5 6Z" fill="#3b82f6" />
    <path d="M11.5 9L9 11.5L11.5 14L14 11.5L11.5 9Z" fill="#60a5fa" />
  </svg>
);

const AutocabLogo = () => (
  <div className="app-card__logo-container app-card__logo-container--green">
    <span className="autocab-text-top">DG</span>
    <span className="autocab-text-bottom">TAXIS</span>
  </div>
);

const DriverPortalLogo = () => (
  <svg className="app-card__logo-svg" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="#fbbf24" strokeWidth="2.5" fill="rgba(251, 191, 36, 0.05)" />
    <circle cx="12" cy="12" r="3" fill="#fbbf24" />
    <path d="M12 2V9" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
    <path d="M4 16.5L10 13.5" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
    <path d="M20 16.5L14 13.5" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const SlackLogo = () => (
  <svg className="app-card__logo-svg" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="7" width="5" height="5" rx="2.5" fill="#36C5F0" />
    <rect x="8" y="2" width="5" height="10" rx="2.5" fill="#36C5F0" />
    <rect x="17" y="2" width="5" height="5" rx="2.5" fill="#2EB67D" />
    <rect x="14" y="8" width="10" height="5" rx="2.5" fill="#2EB67D" />
    <rect x="17" y="17" width="5" height="5" rx="2.5" fill="#ECB22E" />
    <rect x="8" y="14" width="5" height="10" rx="2.5" fill="#ECB22E" />
    <rect x="2" y="17" width="5" height="5" rx="2.5" fill="#E01E5A" />
    <rect x="2" y="14" width="10" height="5" rx="2.5" fill="#E01E5A" />
  </svg>
);

const NudgLogo = () => (
  <div className="app-card__logo-container app-card__logo-container--nudg">
    <span className="nudg-text-n">n</span>
    <span className="nudg-text-udg">udg</span>
    <span className="nudg-dot" />
  </div>
);

const GmailLogo = () => (
  <svg className="app-card__logo-svg" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4Z" stroke="#ef4444" strokeWidth="2" strokeLinejoin="round"/>
    <path d="M22 6L12 13L2 6" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GoogleDriveLogo = () => (
  <svg className="app-card__logo-svg" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 2L2 13.5L5.5 19.5L12 8.5L8.5 2Z" fill="#3b82f6" />
    <path d="M15.5 2H8.5L12 8.5L19 8.5L15.5 2Z" fill="#10b981" />
    <path d="M12 8.5L5.5 19.5H18.5L22 13.5L12 8.5Z" fill="#fbbf24" />
  </svg>
);

const DefaultLogo = () => (
  <svg className="app-card__logo-svg" width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="3" stroke="var(--accent-emerald)" strokeWidth="2" fill="var(--accent-emerald-glow)"/>
    <path d="M12 8V16M8 12H16" stroke="var(--accent-emerald)" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

function AppCard({ app, onToggleFavorite, onDeleteApp }) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const renderLogo = (logoName) => {
    switch (logoName?.toLowerCase()) {
      case "jira":
        return <JiraLogo />;
      case "autocab":
      case "autocab/dg taxis":
        return <AutocabLogo />;
      case "driver":
      case "driver portal":
      case "drivers portal":
      case "drivers por.":
        return <DriverPortalLogo />;
      case "slack":
        return <SlackLogo />;
      case "nudg":
        return <NudgLogo />;
      case "gmail":
        return <GmailLogo />;
      case "drive":
      case "google drive":
      case "google dri.":
        return <GoogleDriveLogo />;
      default:
        return <DefaultLogo />;
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="app-card glass-card">
      {/* 3-Dot Dropdown Trigger */}
      <div className="app-card__menu-container" ref={dropdownRef}>
        <button
          className="app-card__menu-btn"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          aria-label="App actions"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="5" r="1.5" fill="currentColor" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" />
            <circle cx="12" cy="19" r="1.5" fill="currentColor" />
          </svg>
        </button>

        {dropdownOpen && (
          <div className="app-card__dropdown glass-card">
            <button
              className="app-card__dropdown-item"
              onClick={() => {
                onToggleFavorite(app.id);
                setDropdownOpen(false);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
              {app.isFavorite ? "Remove Favorite" : "Add to Favorites"}
            </button>
            <div className="app-card__dropdown-divider" />
            <button
              className="app-card__dropdown-item app-card__dropdown-item--danger"
              onClick={() => {
                onDeleteApp(app.id);
                setDropdownOpen(false);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              Delete App
            </button>
          </div>
        )}
      </div>

      {/* Brand Logo */}
      <div className="app-card__logo-wrapper">
        {renderLogo(app.logo)}
      </div>

      {/* App Name */}
      <h3 className="app-card__title">{app.name}</h3>
    </div>
  );
}

export default AppCard;
